<template>
  <mock-up-main>
    <div class="mockup-viewport-wrapper">
      <v-card class="mx-auto bank-view-root" max-width="500" :style="pageStyle">
        
        <!-- مودال کیوآر -->
        <universal-qr-modal
          v-model="showQrDialog"
          :title="value?.infoUser?.nameCollection || value.visitName || 'کیوکارت'"
          :subtitle="'برای دریافت و اسکن اطلاعات حساب، کد زیر را اسکن کنید'"
          :identifier="value?.identifier || value?.id || ''"
          :profile-avatar="value?.infoUser?.logoImage || ''"
          :primary-color="primaryActionColor"
          :svg-data="qrSvgDataUrl"
        />

        <!-- دکمه شناور اسکن QR -->
        <v-btn
          fab
          x-small
          elevation="4"
          class="qr-floating-btn"
          title="مشاهده و اسکن QR کد"
          @click="showQrDialog = true"
        >
          <v-icon size="18">mdi-qrcode-scan</v-icon>
        </v-btn>

        <!-- بخش اسکرول‌پذیر (هدر + محتوای کارت‌ها) -->
        <div class="bank-scrollable-area">
          <!-- تصویر کاور هدر -->
          <v-img
            v-if="value?.infoUser?.background"
            height="160px"
            :src="value.infoUser.background"
            alt="پس‌زمینه هدر"
            class="align-end flex-shrink-0"
          >
            <div class="header-overlay"></div>
          </v-img>

          <!-- اطلاعات پروفایل/فروشگاه -->
          <div
            class="profile-header text-center px-4"
            :class="{ 'with-bg': !!value?.infoUser?.background }"
          >
            <v-avatar size="76" class="profile-avatar elevation-4" v-if="value?.infoUser?.logoImage">
              <v-img :src="value?.infoUser?.logoImage" />
            </v-avatar>

            <h3 :style="textStyle" class="font-weight-bold mt-3 mb-2">
              {{ value?.infoUser?.nameCollection }}
            </h3>
            <p :style="textStyle" class="mb-2">
              {{ value?.infoUser?.subTitle }}
            </p>
          </div>

          <!-- محتوای کارت‌ها -->
          <div class="px-3 content-wrapper mt-3" :style="scaleStyle">
            <!-- ۱. حالت آکاردئونی -->
            <v-expansion-panels
              v-if="displayType === 'accordion'"
              accordion
              flat
              class="bank-accordion rounded-xl"
            >
              <v-expansion-panel
                v-for="(card, index) in cardList"
                :key="'acc-' + (card.id || index)"
                class="mb-2 rounded-xl overflow-hidden"
              >
                <v-expansion-panel-header class="py-3 px-4 font-weight-bold">
                  <div class="d-flex align-center">
                    <v-avatar size="24" class="me-2" tile>
                      <img
                        :src="card.bank_logo || getBankLogo(card)"
                        alt="logo"
                        style="object-fit: contain"
                      />
                    </v-avatar>
                    <span>{{ card.bank_name || card.bankName || 'کیوکارت ' + (index + 1) }}</span>
                    <span v-if="card.owner_name || card.ownerName" class="ms-2 grey--text text-caption font-weight-regular">
                      ({{ card.owner_name || card.ownerName }})
                    </span>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="pt-2 px-0">
                  <bank-card-item 
                    :card="card" 
                    :has-image-bg="hasCardImage(card)"
                    :text-color="getCardTextColor(card)" 
                    :default-bg="getCardBackground(card)" 
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- ۲. حالت اسلایدری -->
            <div v-else-if="displayType === 'slider'" class="bank-card-carousel-wrapper">
              <v-carousel
                v-model="currentSlide"
                height="auto"
                hide-delimiter-background
                show-arrows-on-hover
                :show-arrows="cardList.length > 1"
                class="bank-carousel"
              >
                <template v-slot:prev="{ on, attrs }">
                  <v-btn icon x-small class="carousel-arrow prev-btn" v-bind="attrs" v-on="on">
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                </template>
                <template v-slot:next="{ on, attrs }">
                  <v-btn icon x-small class="carousel-arrow next-btn" v-bind="attrs" v-on="on">
                    <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                </template>

                <v-carousel-item
                  v-for="(card, index) in cardList"
                  :key="'slide-' + (card.id || index)"
                >
                  <div class="pa-1">
                    <bank-card-item
                      :card="card"
                      :has-image-bg="hasCardImage(card)"
                      :default-bg="getCardBackground(card)"
                      :text-color="getCardTextColor(card)"
                    />
                  </div>
                </v-carousel-item>
              </v-carousel>

              <div v-if="cardList.length > 1" class="text-center mt-1 grey--text text-caption font-weight-medium">
                {{ currentSlide + 1 }} از {{ cardList.length }}
              </div>
            </div>

            <!-- ۳. حالت پیش‌فرض (لیستی) -->
            <div v-else class="cards-list">
              <bank-card-item
                v-for="(card, index) in cardList"
                :key="'list-' + (card.id || index)"
                :card="card"
                :has-image-bg="hasCardImage(card)"
                :default-bg="getCardBackground(card)"
                class="mb-3"
                :text-color="getCardTextColor(card)"
              />
            </div>

            <!-- در صورت خالی بودن -->
            <div v-if="!cardList.length" class="text-center py-6 grey--text">
              <v-icon size="48" color="grey lighten-1">mdi-credit-card-outline</v-icon>
              <div class="mt-2 text-caption">هیچ کیوکارت ثبت نشده است</div>
            </div>
          </div>
        </div>

        <!-- فوتر ثابت که همیشه در پایین کادر گوشی قرار می‌گیرد -->
        <a 
          href="https://qrland.ir" 
          target="_blank" 
          rel="noopener noreferrer"
          class="fixed-footer d-flex flex-column align-center pb-2 pt-2 px-4 text-decoration-none" 
        >  
          <div class="d-flex align-center justify-center opacity-70" >
            <img src="media/cr_logo.webp" alt="Terminal Ads Logo" height="18" style="filter: grayscale(30%);" />
            <span class="text-caption font-weight-medium mr-2" style="color: #666;">قدرت گرفته از کیوآر لند</span>
          </div>
        </a>
      </v-card>
    </div>

  </mock-up-main>
</template>

<script>
import MockUpMain from "@/view/pages/businessPage/components/mockUp.vue";
import BankCardItem from "./component/BankCardItem.vue";
import { getBankInfoByCardNumber } from "./component/js/getBankNameByBankNumber.js";
import UniversalQrModal from "../../components/UniversalQrModal.vue";

const BANK_CARD_THEMES = {
  // نئوبانک‌ها
  blue: {
    names: ["بلو", "بلوبانک", "بلو بانک"],
    designs: [{ file: "blue.webp", text: "#ffffff" }]
  },
  bankino: {
    names: ["بانکینو"],
    designs: [{ file: "bankino.webp", text: "#ffd700" }]
  },

  // بانک‌ها با چند طرح
  ayandeh: {
    names: ["آینده"],
    designs: [
      { file: "Ayandeh.webp", text: "#ffffff" },
      { file: "Ayandeh_1.webp", text: "#ffffff" },
      { file: "Ayandeh_2.webp", text: "#ffffff" }
    ]
  },
  saderat: {
    names: ["صادرات"],
    designs: [
      { file: "Saderat.webp", text: "#1B045A" },
      { file: "Saderat_1.webp", text: "#ffd700" }
    ]
  },
  saman: {
    names: ["سامان"],
    designs: [
      { file: "Saman.webp", text: "#1a1a1a" },
      { file: "Saman_1.webp", text: "#ffffff" },
      { file: "Saman_2.webp", text: "#ffffff" }
    ]
  },
  tejarat: {
    names: ["تجارت"],
    designs: [
      { file: "Tejarat.webp", text: "#ffffff" },
      { file: "Tejarat_1.webp", text: "#ffffff" }
    ]
  },
  mellat: {
    names: ["ملت"],
    designs: [
      { file: "Mellat.webp", text: "#ffffff" },
      { file: "Mellat_1.webp", text: "#ffffff" }
    ]
  },
  melli: {
    names: ["ملی"],
    designs: [
      { file: "Melli.webp", text: "#ffffff" },
      { file: "Melli_1.webp", text: "#1a1a1a" },
      { file: "meli-seporde.webp", text: "#ffffff" }
    ]
  },
  keshavarzi: {
    names: ["کشاورزی"],
    designs: [
      { file: "Keshavarzi.webp", text: "#ffd700" },
      { file: "Keshavarzi_1.webp", text: "#1a1a1a" },
      { file: "Keshavarzi_2.webp", text: "#ffffff" },
      { file: "keshavarzi-mehr.webp", text: "#1a1a1a" }
    ]
  },
  maskan: {
    names: ["مسکن"],
    designs: [
      { file: "Maskan.webp", text: "#1a1a1a" },
      { file: "Maskan_1.webp", text: "#1a1a1a" },
      { file: "Maskan_2.webp", text: "#1a1a1a" }
    ]
  },
  gardeshgari: {
    names: ["گردشگری"],
    designs: [
      { file: "Gardeshgari.webp", text: "#1a1a1a" },
      { file: "Gardeshgari_1.webp", text: "#1a1a1a" }
    ]
  },
  refah: {
    names: ["رفاه"],
    designs: [
      { file: "Refah.webp", text: "#ffffff" },
      { file: "Refah_1.webp", text: "#1a1a1a" }
    ]
  },
  postbank: {
    names: ["پست بانک", "پست"],
    designs: [
      { file: "Postbank.webp", text: "#ffffff" },
      { file: "Postbank_1.webp", text: "#1a1a1a" }
    ]
  },
  resalat: {
    names: ["رسالت"],
    designs: [
      { file: "Resalat.webp", text: "#ffffff" },
      { file: "Resalat_2.webp", text: "#ffffff" }
    ]
  },
  mehr: {
    names: ["مهر", "مهر ایران", "قرض الحسنه مهر"],
    designs: [
      { file: "Mehr_Iran.webp", text: "#ffffff" },
      { file: "Mehr_Iran_1.webp", text: "#ffffff" }
    ]
  },
  shahr: {
    names: ["شهر"],
    designs: [
      { file: "Shahr.webp", text: "#ffffff" },
      { file: "Shahr_1.webp", text: "#ffffff" }
    ]
  },

  // بانک‌ها با تک طرح
  pasargad: {
    names: ["پاسارگاد"],
    designs: [{ file: "Pasargad.webp", text: "#ffd700" }]
  },
  parsian: {
    names: ["پارسیان"],
    designs: [{ file: "Parsian.webp", text: "#1a1a1a" }]
  },
  sepah: {
    names: ["سپه"],
    designs: [{ file: "Sepah.webp", text: "#1a1a1a" }]
  },
  sina: {
    names: ["سینا"],
    designs: [{ file: "Sina.webp", text: "#1a1a1a" }]
  },
  dey: {
    names: ["دی"],
    designs: [{ file: "Dey.webp", text: "#1a1a1a" }]
  },
  eghtesad: {
    names: ["اقتصاد نوین"],
    designs: [{ file: "Eghtesad_Novin.webp", text: "#ffffff" }]
  },
  ghavamin: {
    names: ["قوامین"],
    designs: [{ file: "Ghavamin.webp", text: "#1a1a1a" }]
  },
  ansar: {
    names: ["انصار"],
    designs: [{ file: "Ansar.webp", text: "#ffffff" }]
  },
  hekmat: {
    names: ["حکمت"],
    designs: [{ file: "Hekmat.webp", text: "#ffffff" }]
  },
  iran_zamin: {
    names: ["ایران زمین"],
    designs: [{ file: "Iran_Zamin.webp", text: "#ffffff" }]
  },
  khavar_mianeh: {
    names: ["خاورمیانه", "خاور میانه"],
    designs: [{ file: "Khavar_Mianeh.webp", text: "#1a1a1a" }]
  },
  kosar: {
    names: ["کوثر"],
    designs: [{ file: "Kosar.webp", text: "#ffffff" }]
  },
  melal: {
    names: ["ملل"],
    designs: [{ file: "Melall.webp", text: "#1a1a1a" }]
  },
  mofid: {
    names: ["مفید"],
    designs: [{ file: "mofid.webp", text: "#ffffff" }]
  },
  noor: {
    names: ["نور"],
    designs: [{ file: "Noor.webp", text: "#1a1a1a" }]
  },
  sanat_madan: {
    names: ["صنعت و معدن"],
    designs: [{ file: "Sanat_Madan.webp", text: "#1a1a1a" }]
  },
  sarmayeh: {
    names: ["سرمایه"],
    designs: [{ file: "Sarmayeh.webp", text: "#1a1a1a" }]
  },
  tosee_saderat: {
    names: ["توسعه صادرات"],
    designs: [{ file: "Tosee_Saderat.webp", text: "#1a1a1a" }]
  },
  tosee_taavon: {
    names: ["توسعه تعاون"],
    designs: [{ file: "Tosee_Taavon.webp", text: "#1a1a1a" }]
  },
  caspian: {
    names: ["کاسپین"],
    designs: [{ file: "Caspian.webp", text: "#1a1a1a" }]
  }
};

export default {
  name: "viewMultiCard",
  components: {
    MockUpMain,
    BankCardItem,
    UniversalQrModal,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    qrSvgDataUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showQrDialog: false,
      currentSlide: 0,
    };
  },
  computed: {
    primaryActionColor() {
      return this.value?.pallete?.bodyColor || "#1976D2";
    },
    displayType() {
      return this.value?.cardBank?.displayType || "list";
    },
    sizeLevel() {
      return this.value?.cardBank?.sizeLevel || 2;
    },
    currentCardTheme() {
      return this.value?.cardBank?.cardTheme || "white";
    },
    scaleStyle() {
      const scaleMap = { 1: 1.0, 2: 0.95, 3: 0.88, 4: 0.82 };
      const scale = scaleMap[this.sizeLevel] || 0.95;
      return {
        transform: `scale(${scale})`,
        transformOrigin: "top center",
        transition: "all 0.25s ease-in-out",
      };
    },
    pageStyle() {
      const bg = this.value?.pallete?.bodyColor || "#f4f6f9";
      return { background: bg };
    },
    defaultFallbackBg() {
      return this.value?.pallete?.buttonColor || "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)";
    },
    textStyle() {
      const color = this.value?.pallete?.textColor || "#2c3e50";
      return { color };
    },
    cardList() {
      return this.value?.cardBank?.list || [];
    },
  },
  methods: {
    // ۱. پیدا کردن کلید بانک از آبجکت دیکشنری
    getBankKey(card) {
      const num = (card.cart_number || "").toString().replace(/\D/g, "");
      if (num.length >= 6) {
        try {
          const info = getBankInfoByCardNumber(num);
          if (info?.key && BANK_CARD_THEMES[info.key]) return info.key;
          if (info?.name) {
            const keyMatch = Object.keys(BANK_CARD_THEMES).find((k) =>
              BANK_CARD_THEMES[k].names.some((n) => info.name.includes(n))
            );
            if (keyMatch) return keyMatch;
          }
        } catch (e) {}
      }

      const name = (card.bank_name || card.bankName || "").toLowerCase();
      if (!name) return null;

      for (const [key, config] of Object.entries(BANK_CARD_THEMES)) {
        if (config.names.some((alias) => name.includes(alias.toLowerCase()))) {
          return key;
        }
      }
      return null;
    },

    // ۲. انتخاب طرح بر اساس تکرار بانک در لیست کارت‌ها
    getCardDesign(card) {
      const bankKey = this.getBankKey(card);
      if (!bankKey || !BANK_CARD_THEMES[bankKey]?.designs?.length) return null;

      const designs = BANK_CARD_THEMES[bankKey].designs;
      
      const sameBankCards = this.cardList.filter((c) => this.getBankKey(c) === bankKey);
      const cardIndexInSameBank = sameBankCards.indexOf(card);
      const designIndex = (cardIndexInSameBank >= 0 ? cardIndexInSameBank : 0) % designs.length;

      return designs[designIndex];
    },

    // ۳. بررسی داشتن عکس پس‌زمینه
    hasCardImage(card) {
      if (card.bank_background) return true;
      return !!this.getCardDesign(card);
    },

    // ۴. دریافت پس‌زمینه کارت
    getCardBackground(card) {
      if (card.bank_background) {
        return `url('${card.bank_background}')`;
      }
      if (card.bank_color) return card.bank_color;

      const design = this.getCardDesign(card);
      if (design?.file) {
        return `url('/bankCart/${design.file}')`;
      }

      return this.getBankColor(card);
    },


    // ۵. دریافت رنگ متن متناسب با همان طرح انتخابی
    getCardTextColor(card) {
      const design = this.getCardDesign(card);
      if (design?.text) {
        return design.text === "#1a1a1a" ? "dark" : "white";
      }

      // اگر عکس نداشت بر اساس قوانین کلی بانک
      const name = (card.bank_name || card.bankName || "").toLowerCase();
      if (name.includes("پاسارگاد") || name.includes("سرمایه")) {
        return "dark";
      }
      return this.currentCardTheme || "white";
    },

    // ۶. رنگ‌های پیش‌فرض
    getBankColor(card) {
      if (card.bank_color) return card.bank_color;

      const num = (card.cart_number || "").toString().replace(/\D/g, "");
      if (num.length >= 6) {
        try {
          const info = getBankInfoByCardNumber(num);
          if (info && (info.gradient || info.color)) {
            return info.gradient || info.color;
          }
        } catch (e) {}
      }

      const name = (card.bank_name || card.bankName || "").toLowerCase();

      if (name.includes("بلوبانک") || name.includes("بلو بانک") || name === "بلو")
        return "linear-gradient(135deg, #1688ffA4 0%, #0752c7A4 100%)";
      if (name.includes("ویپاد"))
        return "linear-gradient(135deg, #00a884A4 0%, #005b50A4 100%)";
      if (name.includes("بانکینو"))
        return "linear-gradient(135deg, #8b5cf6A4 0%, #4c1d95A4 100%)";
      if (name.includes("اقتصاد نوین"))
        return "linear-gradient(135deg, #5c2e91A4 0%, #471c6fA4 100%)";
      if (name.includes("ایران زمین"))
        return "linear-gradient(135deg, #16a34aA4 0%, #14532dA4 100%)";
      if (name.includes("پارسیان"))
        return "linear-gradient(135deg, #a10f1fA4 0%, #7a0b14A4 100%)";
      if (name.includes("پاسارگاد"))
        return "linear-gradient(135deg, #ffc110A4 0%, #e59a00A4 100%)";
      if (name.includes("تجارت"))
        return "linear-gradient(135deg, #2563ebA4 0%, #172554A4 100%)";
      if (name.includes("رفاه"))
        return "linear-gradient(135deg, #1e7a00A4 0%, #155a00A4 100%)";
      if (name.includes("شهر"))
        return "linear-gradient(135deg, #dd0000A4 0%, #b20000A4 100%)";
      if (name.includes("صادرات"))
        return "linear-gradient(135deg, #29166fA4 0%, #1e1158A4 100%)";
      if (name.includes("صنعت و معدن"))
        return "linear-gradient(135deg, #0f317eA4 0%, #0a1f5dA4 100%)";
      if (name.includes("قرض الحسنه مهر") || name.includes("مهر ایران"))
        return "linear-gradient(135deg, #00a653A4 0%, #008542A4 100%)";
      if (name.includes("رسالت"))
        return "linear-gradient(135deg, #0d9488A4 0%, #134e4aA4 100%)";
      if (name.includes("کشاورزی"))
        return "linear-gradient(135deg, #112c09A4 0%, #0e2307A4 100%)";
      if (name.includes("مسکن"))
        return "linear-gradient(135deg, #ff0100A4 0%, #cc0000A4 100%)";
      if (name.includes("ملت"))
        return "linear-gradient(135deg, #d12236A4 0%, #a10f26A4 100%)";
      if (name.includes("ملی"))
        return "linear-gradient(135deg, #0d324dA4 0%, #7f5a83A4 100%)";
      if (name.includes("پست بانک"))
        return "linear-gradient(135deg, #008840A4 0%, #006b2eA4 100%)";
      if (name.includes("سپه"))
        return "linear-gradient(135deg, #0093ddA4 0%, #0077b3A4 100%)";
      if (name.includes("سامان"))
        return "linear-gradient(135deg, #00aae8A4 0%, #008bb5A4 100%)";
      if (name.includes("آینده"))
        return "linear-gradient(135deg, #78350fA4 0%, #292016A4 100%)";

      return this.defaultFallbackBg || "linear-gradient(135deg, #334155A4 0%, #0f172aA4 100%)";
    },

    // ۷. لوگوی پیش‌فرض
    getBankLogo(card) {
      const num = (card.cart_number || "").toString().replace(/\D/g, "");
      if (num.length >= 6) {
        try {
          const info = getBankInfoByCardNumber(num);
          if (info?.logo) return info.logo;
        } catch (e) {}
      }
      return "/media/defaults/qrLogo.webp";
    },
  },

};
</script>

<style scoped>
.mockup-viewport-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.bank-view-root {
  height: 100%;
  max-height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: none !important;
  overflow: hidden;
}

/* اسکرول روان محتوا داخل کادر گوشی */
.bank-scrollable-area {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 60px; /* فضای خالی جهت جلوگیری از رفتن کارت آخر زیر فوتر ثابت */
  -webkit-overflow-scrolling: touch;
}

.bank-scrollable-area::-webkit-scrollbar {
  width: 4px;
}
.bank-scrollable-area::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

/* فوتر چسبیده و بلوری در پایین */
.fixed-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  z-index: 15;
  box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.08);
  opacity: 0.7;
}

.header-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.6) 100%);
  height: 100%;
}

.profile-header.with-bg {
  margin-top: -38px;
  position: relative;
  z-index: 2;
}

.profile-avatar {
  border: 3px solid #ffffff;
  background-color: #fff;
}

.bank-accordion >>> .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.03) !important;
}

.bank-accordion >>> .v-expansion-panel-content__wrap {
  padding: 8px 12px 12px 12px !important;
}

.bank-carousel >>> .v-carousel__controls {
  position: relative;
  height: 36px;
  background: transparent !important;
}

.bank-carousel >>> .v-btn--icon.v-size--small {
  width: 14px;
  height: 14px;
  margin: 0 3px;
}

.bank-carousel >>> .v-carousel__controls__item .v-icon {
  font-size: 10px !important;
  opacity: 0.45;
}

.bank-carousel >>> .v-carousel__controls__item--active .v-icon {
  opacity: 1;
  color: #1976d2 !important;
  transform: scale(1.3);
}

.carousel-arrow {
  background: rgba(255, 255, 255, 0.85) !important;
  color: #333 !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.qr-floating-btn {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 20;
  background-color: rgba(255, 255, 255, 0.9) !important;
}
/* فاصله خالی بالا برای کارت‌هایی که خود تصویر، لوگو و چیپ دارد */
.card-image-top-spacer {
  /* در کارت‌های استاندارد، شماره کارت از ارتفاع 50 درصدی شروع می‌شود */
  height: 48%; 
  width: 100%;
  flex-shrink: 0;
}

/* --- استایل‌های اختصاصی وقتی پس‌زمینه عکس است --- */
.is-image-bg .card-inner-layout {
  /* تنظیم فاصله عناصر روی عکس */
  padding-bottom: 2px !important;
}

/* در حالت عکس، فوتر نباید بک‌گراند بلور و خط داشته باشد چون خود عکس کادربندی دارد */
.is-image-bg .card-footer {
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border-top: none !important;
  padding-bottom: 4px !important;
  padding-top: 0 !important;
}

/* برای خواناتر شدن متن روی طرح‌های شلوغ بانکی */
.is-image-bg.text-mode-dark .card-number,
.is-image-bg.text-mode-dark .holder-name {
  text-shadow: 0 0 5px rgba(255, 255, 255, 0.8) !important;
}
.is-image-bg.text-mode-white .card-number,
.is-image-bg.text-mode-white .holder-name {
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.9) !important;
}

</style>
